// MACHINERY V2 (Stroje) — Duplicate payload builder (M2.2 §9 D4, + A1 fix).
//
// Isolated in its own module because it carries the client-side fixes to a
// shipped bug, and a reviewer should be able to read the whole fix in one
// screen without a card component around it.
//
// ===========================================================================
// A1 — "This machine cannot be duplicated" on 17 of 75 live machines
// ===========================================================================
// SYMPTOM: duplicating Manurhin K'MX 413 raised
// `v2.fleet.duplicateMissingFields` and never fired a request. SBL 500 CNC
// duplicated fine.
//
// CAUSE: the previous guard aborted when ANY of typeId / manufacturerId /
// operationTypeId / controlSystemBrandId was falsy, asserting all four "would
// 400 server-side". That assertion was never checked against the server. Two
// of the four are not required by the server at ALL, and the other two are
// required only for CUSTOM machines. The actual contract:
//
//   Machine.OperationTypeId       Guid?  MachineConfiguration.cs:54-59 IsRequired(false)
//   Machine.ControlSystemBrandId  Guid?  MachineConfiguration.cs:68-73 IsRequired(false)
//     -> CheckChangeModelAsync (MachinesService.cs:567-606) never tests either
//        one. There is no [Required], no FluentValidation and no ModelState
//        check anywhere on the create path. The server does not reject a
//        machine for missing them — it stores NULL.
//   Machine.TypeId                Guid   (NOT NULL column)
//   Machine.ManufacturerId        Guid   (NOT NULL column)
//     -> MachinesService.cs:574 / :582 throw "Machine type is required" /
//        "Manufacturer is required" ONLY inside `if (!model.CatalogModelId
//        .HasValue)`. With a catalog link, ResolveCatalogFieldsAsync
//        (MachinesService.cs) derives TypeId from the catalog row's
//        MachineTypeId, CNC fallback (MACH-TYPE-DEFAULT-1), and
//        ManufacturerId (straight off CatalogMachineModel.CatalogManufacturerId)
//        before that check runs.
//
// K'MX 413 stores ControlSystemBrandId = NULL: its catalog row has an empty
// CncBrand and CncModel "4 + 2", which CncBrandMapper cannot resolve, so the
// server's own best-effort resolver logged a warning and moved on
// (MachinesService.cs:370-377) when the machine was created. The client then
// refused to copy a machine the server had been perfectly happy to create —
// and it is the ONLY one of the four that ever fires: across the live fleet
// typeId, manufacturerId and operationTypeId are non-null on all 75 machines,
// while 17 have a null controlSystemBrandId.
//
// FIX: block only on what the server genuinely rejects — a CUSTOM machine (no
// catalogModelId) missing typeId or manufacturerId. Every other reference id
// is copied when present and OMITTED when absent.
//
// WHY OMIT RATHER THAN RE-SOURCE FROM `catalogModel`: for a catalog-linked
// machine, omitting is not lossy. It hands the field back to
// ResolveCatalogFieldsAsync — the same server routine that populated the
// ORIGINAL from the same catalog row — which resolves strictly better than
// this module could (CncBrandMapper misspelling/product-line handling, EDM
// sub-type and category mapping). Re-deriving any of it here would be a weaker
// copy of server logic, free to drift. It is also not available to re-derive:
// CatalogModelListItem exposes `operationTypeName` / `controlSystemBrandName`
// (models.ts:235,237) — display names, not Guids.
//
// STATED CONSEQUENCE — a copy can end up MORE COMPLETE than its original.
// Omission hands the field back to the server, so a duplicate is a
// RE-RESOLUTION against today's catalog, not a byte-for-byte clone. A catalog
// machine created before a resolver branch shipped can hold NULL while its
// copy gets a value. Traced case: an older turning machine stored
// OperationTypeId = NULL; duplicating it now runs the resolver, whose explicit
// CategoryCode map ("turning-machines" -> "Turning",
// MachinesService.cs:267-274, applied at :464-471) fills it — so the COPY
// shows an operation type the ORIGINAL still leaves blank. Same shape for
// AxisConfigurationId from catalog NumAxes (:520-543), and for System from
// CncModel (:381-419), the latter only when the copied System is empty, since
// the payload does send the original's value.
//
// This is INTENDED, not a defect: the copy reflects what the catalog actually
// says instead of inheriting a stale gap. It is also USER-VISIBLE — two cards
// side by side can legitimately differ, so Duplicate must not be described as
// an exact clone. K'MX 413 is NOT in this class: its control-system brand is
// genuinely unresolvable from its own catalog row and its other reference ids
// are already populated, so its copy matches the original exactly.
// ===========================================================================
//
// THE TWO D4 FIXES (live /machinery omits both — deliberately NOT touched):
//   1. catalogModelId — the catalog-vs-custom DISCRIMINATOR (data package §12).
//      Dropping it silently converts a duplicated CATALOG machine into a
//      CUSTOM one: it loses the Specifications tab, the catalog image
//      fallback, the parent-brand breadcrumb, key specs and its MIDAS
//      readiness row. Nothing warns the user; the copy just quietly becomes a
//      lesser machine.
//   2. locationId — same omission class. The duplicate lands with no location,
//      so it drops out of every location filter/grouping and out of Worker
//      location scoping until someone edits it by hand.
// Both already exist on `ChangeMachine` (models/models.ts:155,158) and the
// create endpoint already persists them. Frontend-only. Ruled in §9 D4.
//
// SCOPE: v2 only. The live handler (components/Machinery.tsx:648-651) carries
// the SAME four-field abort and is broken on the same 17 machines — worse, it
// only `console.error`s, so live duplication fails SILENTLY. Left
// byte-untouched per the §1 guardrail (live /machinery unchanged until
// cutover).
//
// Fields deliberately NOT copied: serialNumber is forced to "" (a serial is
// unique to one physical machine), and files / taperType / the Phase-7F spec
// copies are left to the server to resolve from the catalog link.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { ChangeMachine, Machine } from "../../models/models";

/**
 * A Guid field is OMITTED, never sent as "".
 *
 * These bind to `Guid?` on ChangeMachineViewModel (ChangeMachineViewModel.cs:
 * 9-22). System.Text.Json throws on "", so the request would 400 in model
 * binding — a DIFFERENT and worse failure than not sending the field at all.
 * `undefined` is dropped by JSON.stringify, so the server binds null and its
 * own catalog resolver takes over.
 */
const optionalId = (id?: string): string | undefined => (id ? id : undefined);

/**
 * Build the create payload for "Duplikovať".
 *
 * Returns `null` ONLY when the server would genuinely reject the request: a
 * custom machine (no catalog link) carrying no machine type or no
 * manufacturer. Every other absent reference id is omitted, not vetoed.
 *
 * A rejection that DOES happen server-side still surfaces loudly:
 * `createMachine.rejected` stores the error on `customerMachinery.error`
 * (customerMachinerySlice.ts:211-214) and MachineryToastHost renders it.
 */
export const buildDuplicatePayload = (machine: Machine): ChangeMachine | null => {
  if (!machine) return null;

  const payload: ChangeMachine = {
    model: machine.model,
    // A serial number identifies ONE physical machine — never cloned.
    serialNumber: "",
    typeId: optionalId(machine.type?.id),
    manufacturerId: optionalId(machine.manufacturer?.id),
    customBrandName: machine.customBrandName,
    operationTypeId: optionalId(machine.operationType?.id),
    axisConfigurationId: optionalId(machine.axisConfiguration?.id),
    controlSystemBrandId: optionalId(machine.controlSystemBrand?.id),
    automationLevelId: optionalId(machine.automationLevel?.id),
    system: machine.system,
    version: machine.version,
    productionYear: machine.productionYear,
    description: machine.description,
    numberOfStations: machine.numberOfStations,
    image: machine.image,
    // ---- THE TWO D4 FIXES (absent from the live payload) ----
    // Keeps a duplicated catalog machine CATALOG-LINKED — and, being present,
    // is what lets the server re-resolve every id omitted above.
    catalogModelId: optionalId(machine.catalogModelId),
    // Keeps the duplicate on the same site/hall.
    locationId: optionalId(machine.locationId),
  };

  // The one genuine server-side requirement (MachinesService.cs:569-585): a
  // machine with NO catalog link must carry its own type and manufacturer,
  // because both are NOT NULL columns and there is no catalog row to derive
  // them from. With a catalog link the server derives both itself.
  if (!payload.catalogModelId && (!payload.typeId || !payload.manufacturerId)) {
    return null;
  }

  return payload;
};

export default buildDuplicatePayload;
